import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { Box } from "@mui/material";
import { FC, useMemo } from "react";
import {
  useAllAlgosData,
  useListedAlgo,
} from "../../contexts/listAlgosContext";
import { usePrefillContext } from "../../../../Services/PrefillContextProvider";
import { ProductDefinition } from "../../types";
import { Column, MandatoryColumns, cols } from "../shared/columns";
import { algoToRowData } from "../shared/algoToRowData";
import { gridStyle } from "../shared/gridStyle";
import { SCHEDULED } from "../shared/constants";
import { isGhostbook, useHideGhostbooks } from "../../atoms/hideGhostbooks";

const isProductSelected = (
  selectedProducts: ProductDefinition[],
  row: MandatoryColumns
) => {
  return selectedProducts.some(
    (item) =>
      item.delivery_area_id === row.grid &&
      item.delivery_start === row._delivery_start &&
      item.delivery_end === row._delivery_end
  );
};

export interface AlgoGridProps {
  quickFilterValues?: string[];
}

export const TraderAlgosDatagrid: FC<AlgoGridProps> = ({
  quickFilterValues,
}) => {
  const apiRef = useGridApiRef();

  const { selectedProducts } = usePrefillContext();

  const { selectedAlgos, setSelectedAlgos } = useListedAlgo();

  const { loading, algos: allAlgos } = useAllAlgosData();
  const [hideGhostbooks] = useHideGhostbooks();

  const algos = useMemo(
    () =>
      hideGhostbooks ? allAlgos?.filter((a) => !isGhostbook(a)) : allAlgos,
    [allAlgos, hideGhostbooks]
  );

  const thisGridIds = useMemo(
    () => new Set(algos?.map((a) => a.algo_id) ?? []),
    [algos]
  );

  const selectedIds = useMemo(() => {
    const set = new Set(
      selectedAlgos
        .filter((a) => thisGridIds.has(a.algo_id))
        .map((a) => a.algo_id)
    );
    return set;
  }, [selectedAlgos, thisGridIds]);

  const baseRows: Column[] = useMemo(() => {
    const mappedRows = algos
      ? algos.map((a) => {
          const rowData = algoToRowData(a);

          return {
            ...rowData,
            originalQuantity: a.configuration.quantity,
            row_id: rowData.algo_id,
            isSelected: false, // Will be calculated in getRowClassName
            isRelated: false, // Will be calculated in getRowClassName
          };
        })
      : [];

    // mimic old sort model sorting by displayGrid, state, product, direction within each group
    return mappedRows.sort((a, b) => {
      const gridCompare = a.displayGrid?.displayName?.localeCompare(
        b.displayGrid?.displayName,
        "da",
        {
          numeric: true,
        }
      );

      if (gridCompare !== 0) {
        return gridCompare;
      }

      // Scheduled algos are the ones nobody is watching right now, and this grid
      // now carries every tag's worth of them — keep them below the live ones.
      const aIsScheduled = a.state.toLowerCase() === SCHEDULED;
      const bIsScheduled = b.state.toLowerCase() === SCHEDULED;

      if (aIsScheduled !== bIsScheduled) {
        return aIsScheduled ? 1 : -1;
      }

      const stateCompare = b.state?.localeCompare(a.state, "da", {
        numeric: true,
      });

      if (stateCompare !== 0) {
        return stateCompare;
      }

      const productCompare = a.product?.localeCompare(b.product, "da", {
        numeric: true,
      });

      if (productCompare !== 0) {
        return productCompare;
      }

      const dirA = a.direction ?? "";
      const dirB = b.direction ?? "";
      return dirA?.localeCompare(dirB, "da", { numeric: true });
    });
  }, [algos]);

  const rows: Column[] = useMemo(() => {
    const rows = baseRows.map((row) => {
      const productIsSelected = isProductSelected(selectedProducts, row);
      const algoIsSelected = selectedIds.has(row.algo_id);
      return {
        ...row,
        isSelected: algoIsSelected,
        isRelated: productIsSelected,
        originalQuantity: row.originalQuantity,
      };
    });
    return rows;
  }, [baseRows, selectedProducts, selectedIds]);
  console.log("rows", rows);
  return (
    <Box
      key={"TradersAlgoGrid.gridWidthContainer"}
      sx={{
        backgroundColor: (t) => t.palette.background.paper,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <DataGridPremium
        loading={loading}
        apiRef={apiRef}
        getRowId={(row) => row.row_id}
        getRowClassName={getRowClassName}
        sx={gridStyle}
        onRowSelectionModelChange={(selected) => {
          const isSameSelection =
            selected.ids.size === selectedIds.size &&
            [...selected.ids].every((id) => selectedIds.has(id.toString()));

          if (algos && !isSameSelection) {
            // The model is the whole truth for the algos this grid shows, so
            // those are taken from it outright — keeping the ones it left out
            // would make every click add a row and no ctrl-click ever remove
            // one. Selections made in another area still survive, since the
            // model cannot represent rows the grid is not showing.
            const merged = [
              ...selectedAlgos.filter((a) => !thisGridIds.has(a.algo_id)),
              ...algos.filter((a) => selected.ids.has(a.algo_id)),
            ];

            // only update if something actually changed zzz
            const currentIds = new Set(selectedAlgos.map((a) => a.algo_id));
            const mergedIds = new Set(merged.map((a) => a.algo_id));
            if (
              currentIds.size !== mergedIds.size ||
              ![...currentIds].every((id) => mergedIds.has(id))
            ) {
              setSelectedAlgos(merged);
            }
          }
        }}
        rowSelectionModel={{
          type: "include",
          ids: new Set(selectedAlgos === undefined ? [] : selectedIds),
        }}
        rows={rows || []}
        columns={cols}
        rowHeight={28}
        density="compact"
        // Quick filtering is MUI's own; only the input lives outside the grid,
        // up in the view toolbar where it is shared with the trades grid.
        filterModel={{ items: [], quickFilterValues }}
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        hideFooter
      />
    </Box>
  );
};

const getRowClassName = (params: { row: Column }) => {
  if (params.row.isSelected) {
    return "row-selected";
  }
  //  else if (params.row.isRelated) {
  //   return "related-row";
  // }
  else {
    return `algo-status--${params.row.state}`;
  }
};
